import {
  FieldSpec as FieldSpecSchema,
  isPaymentPreferencesAnswered,
} from "@medbillai/schemas";

import { isRenderableChatField } from "../../utils/chatFieldTypes";
import type { BaseEvent } from "../../utils/types";

/**
 * The prompt that still needs an answer — the latest `requestUserInput` event
 * that is neither resolved server-side nor answered optimistically. Scans from
 * the end so an unrelated event landing after a pending prompt (a SERVICE
 * message, a document finishing) doesn't strand it; the renderer and composer
 * lock to *this* prompt rather than literally the last event. Once the latest
 * prompt is answered there's no active prompt (older prompts are superseded).
 */
export const latestUnresolvedPrompt = (
  events: readonly BaseEvent[] | null | undefined,
  isAnsweredOptimistically: (eventId: string) => boolean,
): BaseEvent | undefined => {
  if (!events) {
    return undefined;
  }
  for (let i = events.length - 1; i >= 0; i--) {
    const event = events[i];
    if (event?.__typename !== "issue__UserInputRequestedEvent_Type") {
      continue;
    }
    // First prompt scanning back is the latest one; if it's already answered,
    // nothing is active.
    if (event.isResolved === true || isAnsweredOptimistically(event.id)) {
      return undefined;
    }
    return event;
  }
  return undefined;
};

/** Whether a prompt renders as its interactive card rather than its question and answer. */
export const isPromptActive = (
  events: readonly BaseEvent[] | null | undefined,
  eventId: string,
  answers: {
    answerFor: (eventId: string) => unknown;
    errorFor: (eventId: string) => string | null;
  },
): boolean =>
  // A held rejection error re-activates the prompt even when a later prompt has
  // superseded it as the latest unresolved one — otherwise the optimistic unlock
  // lets the answer be rejected after the conversation moved on and the error
  // (and the chance to retry) lands nowhere.
  answers.errorFor(eventId) != null ||
  latestUnresolvedPrompt(events, id => answers.answerFor(id) != null)?.id ===
    eventId;

/**
 * True when the latest event is an unresolved `requestUserInput` prompt that
 * disallows free text — the composer is removed so the user must answer via the
 * prompt. Only locks for a field type the renderer can display; otherwise there'd
 * be no input *and* no composer. Defaults to unlocked.
 */
export const isComposerLockedByEvent = (
  event: BaseEvent | undefined,
): boolean => {
  if (event?.__typename !== "issue__UserInputRequestedEvent_Type") {
    return false;
  }
  if (event.freeTextAllowed !== false || event.isResolved === true) {
    return false;
  }
  const parsed = FieldSpecSchema.safeParse(event.fieldSpec);
  return parsed.success && isRenderableChatField(parsed.data.type);
};

/**
 * A guided flow's question. With typed replies on it leaves the composer open:
 * the API takes a message that is exactly one of its labels, or the amount or
 * date it asks for, as the tap, and the agent gets anything else as chat.
 */
export const isFlowQuestion = (event: BaseEvent | undefined): boolean =>
  event?.__typename === "issue__UserInputRequestedEvent_Type" &&
  FieldSpecSchema.safeParse(event.fieldSpec).data?.flowId !== undefined;

/**
 * Whether a deterministic flow is holding this case on a request it raised.
 *
 * A request step — the HIPAA release, payment preferences — raises no prompt
 * event, so `latestUnresolvedPrompt` cannot see it and the composer stays
 * open. That was a hole rather than a decision: the flow owns every turn while
 * it runs, so a message typed here never reaches the agent. It drives another
 * deterministic turn, which re-emits the same step. The user asks a question
 * and is answered with the ask, again.
 *
 * Scoped by `requestKey`, which only the runner stamps (`flow:<id>:<step>`).
 * An agent turn, a staff send and the post-escalation card all leave it empty,
 * and on those surfaces the composer is how the user replies — locking there
 * would take away the only thing they have.
 *
 * Document steps are deliberately NOT covered. They raise real prompts with
 * `freeTextAllowed`, so they are already excluded by the rule above — and they
 * must be: the composer's `+` is the only upload path for the two experiment
 * arms that render no upload card.
 */
/** An unresolved payment-preferences request stamped by a deterministic flow. */
export const isFlowOwnedPaymentRequest = (
  event: BaseEvent | undefined,
  issueLabels?: readonly string[] | null,
): boolean =>
  event?.__typename === "issue__PaymentPreferencesRequestedEvent_Type" &&
  event.paymentRequestKey != null &&
  !isPaymentPreferencesAnswered({
    confirmedAt: event.paymentPrefsConfirmedAt,
    feeAcknowledgedAt: event.feeAcknowledgedAt,
    labels: issueLabels,
  }) &&
  event.paymentPrefsDeclinedAt == null &&
  event.cancelledAt == null;

export const isComposerLockedByFlowRequest = (
  events: readonly BaseEvent[] | null | undefined,
  issueLabels?: readonly string[] | null,
): boolean =>
  (events ?? []).some(event => {
    if (event?.__typename === "issue__TemplateReleaseRequestedEvent_Type") {
      return (
        event.releaseRequestKey != null &&
        event.signing?.signedAt == null &&
        event.release?.grantedAt == null
      );
    }
    return isFlowOwnedPaymentRequest(event, issueLabels);
  });

/**
 * When the latest event is an unresolved free-text-allowed prompt, a composer
 * message *is* the answer: returns the prompt id so the composer routes the text
 * through `provideUserInputAnswer` instead of sending a dangling chat message.
 * Returns undefined otherwise.
 */
export const freeTextAnswerablePromptId = (
  event: BaseEvent | undefined,
): string | undefined => {
  if (event?.__typename !== "issue__UserInputRequestedEvent_Type") {
    return undefined;
  }
  if (event.isResolved === true || event.freeTextAllowed === false) {
    return undefined;
  }
  return event.id;
};
